import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_BASE_URL from '../../apis/api';
import {API_INTG_URL} from "../../apis/api"; 

const api = axios.create({
  baseURL: API_INTG_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Access token expired. Attempting to refresh...");
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token available");
        const response = await api.post("/api/auth/token/refresh/", {
          refresh: refreshToken,
        });
        const { access } = response.data;
        localStorage.setItem("access_token", access);
        setAuthToken(access);
        // Retry the failed request with the new token
        error.config.headers["Authorization"] = `Bearer ${access}`;
        return api.request(error.config);
      } catch (refreshError) {
        console.error("❌ Token refresh failed. Logging out...");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/"; // Redirect to login
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };
export const fetchDashboardSummary = createAsyncThunk(
  'dashboard/fetchSummary',
  async () => {
    const response = await axios.get(`${API_BASE_URL}/shipmentsSummary`);
    return response.data;
  }
);

// Thunk to decrement imports requiring attention and persist to backend
export const decrementImportsRequiringAttentionThunk = createAsyncThunk(
  'dashboard/decrementImportsRequiringAttention',
  async (_, { getState, dispatch }) => {
    const currentCount = getState().dashboard.importsRequiringAttention;

    if (currentCount > 0) {
      // Persist the change to the backend
      await axios.patch(`${API_BASE_URL}/shipmentsSummary`, {
        importsRequiringAttention: currentCount - 1,
      });

      // Dispatch local state update
      dispatch(decrementImportsRequiringAttention());
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    totalImportsInTransit: 0,
    importsRequiringAttention: 0,
    projectedToCloseThisWeek: 0,
    status: 'idle', // loading, succeeded, failed
  },
  reducers: {
    decrementImportsRequiringAttention: (state) => {
      if (state.importsRequiringAttention > 0) {
        state.importsRequiringAttention -= 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.totalImportsInTransit = action.payload.totalImportsInTransit;
        state.importsRequiringAttention = action.payload.importsRequiringAttention;
        state.projectedToCloseThisWeek = action.payload.projectedToCloseThisWeek;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardSummary.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(decrementImportsRequiringAttentionThunk.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(decrementImportsRequiringAttentionThunk.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(decrementImportsRequiringAttentionThunk.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { decrementImportsRequiringAttention } = dashboardSlice.actions;
export default dashboardSlice.reducer;